import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Radio,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import {
  checkDisplayForRentByChangeWarning,
  checkIsApplicationAcceptedValueChanging,
  getRoomForRentFlow,
  getRoomForRentFlowObj,
} from '../../../../containers/CreateListingPage/utils';
import { useForm } from '../../../../hooks';
import { postingConstraints } from '../../../../utils/formValidation';
import { multiFieldsUtil } from '../../../../utils/postingUtils';
import wizardActions from '../../../Wizard/actions';

import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import { FOR_RENT_BY_KEY } from '../../../../containers/CreateListingPage/constants';
import RoomForRent from '../../../../model/RoomForRent';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { asteriskAfterStyles } from '../../../../styles/text';
import { WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';
import ForRentByWarning from './ForRentByWarning';

const propTypes = {};
const defaultProps = {};

const radioControlName = 'forRentBy';
const permissionControlName = 'hasPermissionToCreateListing';

const PERMISSION_ERROR = 'Permission is required';

const RoomForRentByFieldSet = () => {
  const dispatch = useDispatch();
  const { propertyId } = useParams();
  const { enqueueToast } = useToast();
  const { mutateAsync: saveMultipleFieldsPropertyInfoMutate } =
    useRentalManagerUserPropertyMultiEditWebApiHandler_multi();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: {
      isInModalState,
      isSaveComplete,
      roomForRentFlow: contextRoomForRentFlow,
      serverErrorObj,
      shouldSaveOnBlur,
    },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    isApplicationsAccepted,
    listingAlias,
    forRentBy: stateForRentBy,
    roomForRent,
    roomForRent: { currentRoommatesDetails, hasPermissionToCreateListing = false },
  } = useSelector((state) => state.properties.currentProperty);

  const [displayPermissionToCreateError, setDisplayPermissionToCreateError] = useState(false);
  const [displayForRentByWarning, setDisplayForRentByWarning] = useState(false);

  const nextForRentByValRef = React.useRef(null);
  const renderedAlertRef = React.useRef(null);

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      forRentBy: stateForRentBy,
      hasPermissionToCreateListing,
    },
    postingConstraints,
  );

  const roomForRentFlow = getRoomForRentFlow(inputs.forRentBy);

  useEffect(() => {
    if (isSaveComplete) {
      if (displayPermissionToCreateError) {
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

        return;
      }

      saveForRentBy({ value: inputs.forRentBy });
    }
  }, [isSaveComplete]);

  useEffect(() => {
    // hasPermissionToCreateListing can only be true for tenant
    if (inputs.forRentBy !== FOR_RENT_BY_KEY.TENANT) {
      const mockEvent = {
        target: {
          name: permissionControlName,
          value: false,
        },
      };

      handleFormChange(mockEvent);
    }
  }, [inputs.forRentBy]);

  useEffect(() => {
    if (inputs.forRentBy === FOR_RENT_BY_KEY.TENANT && inputs.hasPermissionToCreateListing === false) {
      // use setTimeout to avoid launching modal twice
      setTimeout(() => {
        setDisplayPermissionToCreateError(true);
      }, 0);

      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
    }
  }, [inputs.forRentBy, inputs.hasPermissionToCreateListing]);

  const getForRentByFlowObj = ({ nextForRentByVal, currentForRentByVal }) => {
    const { isRyanFlow: isCurrentlyInRyanFlow, isIreneFlow: isCurrentlyInIreneFlow } =
      getRoomForRentFlowObj(currentForRentByVal);

    const { isRyanFlow: isGoingToRyanFlow, isIreneFlow: isGoingToIreneFlow } = getRoomForRentFlowObj(nextForRentByVal);

    return {
      isGoingToIreneFlow: isGoingToIreneFlow && isCurrentlyInRyanFlow,
      isGoingToRyanFlow: isGoingToRyanFlow && isCurrentlyInIreneFlow,
    };
  };

  const saveMultipleFieldsPropertyInfo = (args) => {
    if (!args?.postData?.property?.listingAlias) {
      if (!propertyId) {
        enqueueToast(<Toast tone="critical">Could not send your request, try again later</Toast>);
        return Promise.resolve();
      }
      args.postData.property.listingAlias = propertyId;
    }
    return saveMultipleFieldsPropertyInfoMutate(args);
  };

  const saveForRentBy = ({ value }) => {
    const dataToSave = {
      property: {
        listingAlias,
        forRentBy: value,
        roomForRent: {
          hasPermissionToCreateListing: inputs.hasPermissionToCreateListing,
        },
      },
    };

    // hasPermissionToCreateListing can only be true for tenant
    if (value !== FOR_RENT_BY_KEY.TENANT) {
      delete dataToSave.property.roomForRent.hasPermissionToCreateListing;
    }

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, 'multi');
          return;
        }
        const {
          listingOutputFieldData: { forRentBy, isApplicationsAccepted, restrictions, roomForRent },
        } = response;

        const roomForRentModel = RoomForRent(roomForRent);

        dispatch(
          PropertiesActions.updateCurrentProperty({
            forRentBy,
            isApplicationsAccepted,
            restrictions,
            roomForRent: roomForRentModel,
          }),
        );

        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err) => {
        updateServerError(err?.message ?? '', 'multi');
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const clearFieldError = () => {
    setDisplayPermissionToCreateError(false);
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return Promise.resolve(false);
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return Promise.resolve(false);
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    if (fieldName === permissionControlName) {
      return savePermissionToCreate(dataToSave);
    }

    saveForRentBy({ value: event.target.value });

    return;
  };

  const savePermissionToCreate = ({ fieldName, value }) => {
    const dataToSave = {
      property: {
        listingAlias,
        roomForRent: {
          [fieldName]: value,
        },
      },
    };

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        dispatch(
          PropertiesActions.updateCurrentProperty({
            roomForRent: {
              ...roomForRent,
              [fieldName]: value,
            },
          }),
        );

        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err) => {
        updateServerError(err?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  /**
   * We want to display a warning when:
   *  1. the persona changes and certain fields are not empty
   *  2. the applications accepted value is changing to be true
   */
  const shouldDisplayChangeConfirmationModal = ({ nextVal, displayForRentByWarning }) => {
    const currentForRentByVal = isInModalState ? stateForRentBy : inputs.forRentBy;
    const currentRoomForRentByFlow = isInModalState ? contextRoomForRentFlow : roomForRentFlow;

    nextForRentByValRef.current = nextVal;

    const { isGoingToIreneFlow, isGoingToRyanFlow } = getForRentByFlowObj({
      nextForRentByVal: nextForRentByValRef.current,
      currentForRentByVal,
    });

    // check persona change
    const isGoingToOtherTypeOfFlow = isGoingToIreneFlow || isGoingToRyanFlow;

    // check applications accepted value is changing
    const { isApplicationAcceptedValueChanging } = checkIsApplicationAcceptedValueChanging({
      isApplicationsAccepted,
      nextForRentByVal: nextForRentByValRef.current,
    });

    /**
     * check for changes in persona or applications accepted change
     * and warning display should be false
     */
    const shouldDisplayModal =
      (isGoingToOtherTypeOfFlow || isApplicationAcceptedValueChanging) && displayForRentByWarning === false;

    // check for fields that are not empty
    const { displayWarning: displayWarningForRentByChange } = checkDisplayForRentByChangeWarning({
      roomForRentFlow: currentRoomForRentByFlow,
      roomForRent: { ...roomForRent },
    });

    // set display warning against non empty fields or accepted value is changed
    const displayWarning = displayWarningForRentByChange || isApplicationAcceptedValueChanging;

    return displayWarning && shouldDisplayModal;
  };

  const handleChangeConfirmationSubmit = () => {
    const mockEvent = {
      target: {
        name: radioControlName,
        value: nextForRentByValRef.current,
      },
    };

    // set is fetching state before saving to handle race condition on next / prev click
    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });
    handleChange(mockEvent);
    setDisplayForRentByWarning(false);
  };

  const handleChangeConfirmationCancelClick = () => {
    setDisplayForRentByWarning(false);
  };

  const handleChange = (event) => {
    const newDisplayForRentByWarning = isInModalState === true ? false : displayForRentByWarning;

    // clear warning
    if (isInModalState === true) {
      setDisplayForRentByWarning(false);
    }

    const shouldDisplayModal = shouldDisplayChangeConfirmationModal({
      nextVal: event.target.value,
      displayForRentByWarning: newDisplayForRentByWarning,
    });

    if (shouldDisplayModal) {
      setDisplayForRentByWarning(true);

      // we want to update the selection within the modal
      if (isInModalState === false) {
        return false;
      }
    }

    clearFieldError();
    clearServerError();
    handleFormChange(event);
    saveDebounced(event);
  };

  const handleCheckboxChange = (event) => {
    const { name } = event.target;

    const mockEvent = {
      target: {
        name,
        value: event.target.checked,
      },
    };

    clearFieldError();
    handleFormChange(mockEvent);
    saveDebounced(mockEvent);
  };

  const renderChangeConfirmationBody = () => {
    return (
      <div data-testid="ChangeConfirmationBody">
        <Heading level={4} css={{ marginBottom: '400' }}>
          Change your selection for who’s listing?
        </Heading>

        <ForRentByWarning roomForRentFlow={roomForRentFlow} nextForRentByVal={nextForRentByValRef.current} />
      </div>
    );
  };

  const renderForRentByWarning = () => {
    renderedAlertRef.current = (
      <Alert tone="warning">
        <ForRentByWarning roomForRentFlow={roomForRentFlow} nextForRentByVal={nextForRentByValRef.current} />
      </Alert>
    );
  };

  const renderPermissionFromPropertyOwnerSection = () => {
    return (
      <WizardFormSectionContainer data-testid="PermissionFromPropertyOwnerSection">
        <WizardHeading level={1} textStyle="heading-sm">
          <Box css={asteriskAfterStyles}>Do you have permission from the property owner/manager?</Box>
        </WizardHeading>

        <FormField>
          {/* eslint-disable-next-line constellation/word-list*/}
          <div>You must have the owner/manager’s permission to create this listing.</div>
          <Box css={{ marginBottom: '400' }} />
          <LabeledControl
            label={
              <Label>
                By checking this box, I confirm I have authority by way of applicable law, an executed lease, or the
                property owner’s explicit consent to publish this listing on Zillow’s platform.
              </Label>
            }
            control={
              <Checkbox
                name={permissionControlName}
                checked={inputs.hasPermissionToCreateListing}
                onChange={handleCheckboxChange}
              />
            }
          />
          {displayPermissionToCreateError && <FormHelp error={true}>{PERMISSION_ERROR}</FormHelp>}
        </FormField>
      </WizardFormSectionContainer>
    );
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS, true), [
    currentRoommatesDetails,
  ]);

  const renderForRentByWarningDebounced = useCallback(
    debounce(renderForRentByWarning, TimeConstants.SELECTION_DEBOUNCE_TIME_MS, true),
    [],
  );

  return (
    <div data-test="RoomForRentByFieldSet">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FieldSet
        legend={<Legend>Listed by</Legend>}
        required={true}
        error={!!errorObj.forRentBy}
        description={<FormHelp error={!!errorObj.forRentBy}>{errorObj.forRentBy}</FormHelp>}
      >
        <LabeledControl
          controlId={FOR_RENT_BY_KEY.OWNER}
          label={<Label>I’m the property owner/manager, and I don’t live on the property</Label>}
          control={
            <Radio
              name={radioControlName}
              value={FOR_RENT_BY_KEY.OWNER}
              checked={inputs.forRentBy === FOR_RENT_BY_KEY.OWNER}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          controlId={FOR_RENT_BY_KEY.OWNER_RESIDENT}
          label={<Label>I’m the property owner/manager, and I do live on the property</Label>}
          control={
            <Radio
              name={radioControlName}
              value={FOR_RENT_BY_KEY.OWNER_RESIDENT}
              checked={inputs.forRentBy === FOR_RENT_BY_KEY.OWNER_RESIDENT}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          controlId={FOR_RENT_BY_KEY.TENANT}
          label={<Label>I’m a current tenant living on the property</Label>}
          control={
            <Radio
              name={radioControlName}
              value={FOR_RENT_BY_KEY.TENANT}
              checked={inputs.forRentBy === FOR_RENT_BY_KEY.TENANT}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          controlId={FOR_RENT_BY_KEY.AGENT}
          label={<Label>I’m an agent/broker/management company, and don’t live on the property</Label>}
          control={
            <Radio
              name={radioControlName}
              value={FOR_RENT_BY_KEY.AGENT}
              checked={inputs.forRentBy === FOR_RENT_BY_KEY.AGENT}
              onChange={handleChange}
            />
          }
        />
      </FieldSet>

      {inputs.forRentBy === FOR_RENT_BY_KEY.TENANT && (
        <>
          <Box css={{ marginBottom: '1200' }} />

          {renderPermissionFromPropertyOwnerSection()}
        </>
      )}

      {/**
       * the for rent by warning stopped rendering via throttle
       * setting the jsx value to a react ref
       */}
      {isInModalState === true && displayForRentByWarning && <>{renderForRentByWarningDebounced()}</>}
      {isInModalState === true && displayForRentByWarning && (
        <div data-testid="ForRentByWarningContainer">{renderedAlertRef.current}</div>
      )}

      {isInModalState === false && displayForRentByWarning && (
        <Modal
          size="sm"
          open={true}
          onOpenChange={(open) => !open && handleChangeConfirmationCancelClick()}
          body={renderChangeConfirmationBody()}
          footer={
            <ButtonGroup aria-label="Change selection actions">
              <TextButton onClick={handleChangeConfirmationCancelClick}>No, don&apos;t change</TextButton>
              <Button emphasis="filled" onClick={handleChangeConfirmationSubmit}>
                Yes, change selection
              </Button>
            </ButtonGroup>
          }
        />
      )}
    </div>
  );
};

RoomForRentByFieldSet.propTypes = propTypes;
RoomForRentByFieldSet.defaultProps = defaultProps;

export default memo(RoomForRentByFieldSet);
